
import React, { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent,CircularProgress, Snackbar, Alert,Typography,Divider,Chip,Stack,Dialog,DialogTitle,DialogContent,DialogActions,TextField,IconButton,ButtonGroup
} from '@mui/material';
import { CheckCircle, ArrowBack, ReportProblem, Payment, CameraAlt,Close,QrCode2
} from '@mui/icons-material';
import { orderAssignmentAPI } from './deliveryAgentAPI';

const DeliveryVerification = ({ order, onBack, onDeliveryConfirmed, onReportIssue }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  // Payment collection state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(order.totalAmount || 0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // ✅ NEW: Track payment status locally
  const [localPaymentStatus, setLocalPaymentStatus] = useState({
    isPaid: order.isPaid,
    paymentCollected: false
  });

  // ✅ Debug logging to track state changes
  useEffect(() => {
    console.log('💳 Payment Status Update:', {
      localIsPaid: localPaymentStatus.isPaid,
      localPaymentCollected: localPaymentStatus.paymentCollected,
      orderIsPaid: order.isPaid,
      needsPayment: !localPaymentStatus.isPaid && 
                    !localPaymentStatus.paymentCollected &&
                    order.paymentMethod && 
                    ['cod', 'cash', 'offline'].includes(order.paymentMethod.toLowerCase())
    });
  }, [localPaymentStatus, order.isPaid, order.paymentMethod]);

  // Photo upload state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  //  CRITICAL FIX: Determine delivery type
  const deliveryType = order.deliveryType || 
                      (order.orderType === 'booking' ? 'sample' : 'medicine');

  // Check if we can deliver
  const isPickedUp = order.deliveryStatus === 'picked_up' || 
                     order.orderStatus === 'sample_collected' ||
                     order.orderStatus === 'sample-collected' ||
                     order.orderStatus === 'report-out-for-delivery' ||
                     order.status === 'report-out-for-delivery';
  
  const isDelivered = order.deliveryStatus === 'delivered' || 
                      order.orderStatus === 'completed' ||
                      order.status === 'report-delivered';
  
  // ✅ UPDATED: Check if payment is needed using local state
  const needsPayment = !localPaymentStatus.isPaid && 
                       !localPaymentStatus.paymentCollected &&
                       order.paymentMethod && 
                       ['cod', 'cash', 'offline'].includes(order.paymentMethod.toLowerCase());
  
  const canDeliver = isPickedUp && !isDelivered && !needsPayment;

  // ✅ Debug: Log delivery eligibility
  useEffect(() => {
    console.log('🚚 Delivery Eligibility Check:', {
      isPickedUp,
      isDelivered,
      needsPayment,
      canDeliver,
      orderStatus: order.deliveryStatus || order.orderStatus || order.status
    });
  }, [isPickedUp, isDelivered, needsPayment, canDeliver, order.deliveryStatus, order.orderStatus, order.status]);

  // Handle payment collection
  const handleOpenPaymentDialog = () => {
    setPaymentAmount(order.totalAmount || 0);
    setPaymentMethod('');
    setPaymentDialogOpen(true);
  };

  const handleCollectPayment = async () => {
    try {
      if (!paymentMethod) {
        setSnackbar({
          open: true,
          message: 'Please select a payment method',
          severity: 'warning'
        });
        return;
      }

      if (!paymentAmount || paymentAmount <= 0) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid amount',
          severity: 'warning'
        });
        return;
      }

      setPaymentLoading(true);
      const userId = sessionStorage.getItem('userId');

      console.log('💰 Collecting payment:', {
        userId,
        orderId: order.id,
        orderType: order.orderType,
        paymentMethod,
        amount: paymentAmount
      });

      const response = await orderAssignmentAPI.collectPayment(
        userId,
        order.id,
        order.orderType,
        paymentMethod,
        paymentAmount
      );

      console.log('✅ Payment collection response:', response.data);

      if (response.data.success) {
        // ✅ UPDATE LOCAL STATE instead of reloading page
        console.log('✅ Payment collected! Updating local state...');
        
        setLocalPaymentStatus({
          isPaid: true,
          paymentCollected: true
        });

        console.log('✅ Local payment status updated:', {
          isPaid: true,
          paymentCollected: true
        });

        setSnackbar({
          open: true,
          message: `Payment of ₹${paymentAmount} collected successfully! You can now confirm delivery.`,
          severity: 'success'
        });

        setPaymentDialogOpen(false);
        
        // Optional: Update the original order object as well
        order.isPaid = true;
        order.paymentStatus = 'paid';
        
        console.log('✅ Payment collection complete. Delivery button should now be enabled.');
        
      } else {
        throw new Error(response.data.message || 'Payment collection failed');
      }

    } catch (error) {
      console.error('❌ Payment collection error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to collect payment';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle photo selection
  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedPhoto(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle photo upload
  const handleUploadPhoto = async () => {
    if (!selectedPhoto) {
      setSnackbar({
        open: true,
        message: 'Please select a photo',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      
      const response = await orderAssignmentAPI.uploadPhoto(order.id, selectedPhoto);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Photo uploaded successfully',
          severity: 'success'
        });
        setSelectedPhoto(null);
        setPhotoPreview(null);
      } else {
        throw new Error(response.data.message || 'Photo upload failed');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload photo',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    try {
      setLoading(true);
      
      const userId = sessionStorage.getItem('userId');
      const token = sessionStorage.getItem('token');

      // Validation
      if (!userId) {
        throw new Error('User ID not found in session');
      }

      if (!order.id || !order.orderType) {
        throw new Error('Order information is incomplete');
      }

      // CRITICAL: Check if order was picked up first
      if (!isPickedUp) {
        throw new Error('Order must be picked up before delivery confirmation');
      }

      // Check if payment is required but not collected
      if (needsPayment) {
        throw new Error('Payment must be collected before delivery confirmation');
      }

      // Check if already delivered
      if (isDelivered) {
        throw new Error(`Order already ${order.deliveryStatus || order.orderStatus || order.status}`);
      }

      // ✅ CRITICAL FIX: Include deliveryType parameter
      console.log('🚚 Confirming delivery:', {
        userId,
        orderId: order.id,
        orderType: order.orderType,
        deliveryType,  
        currentStatus: order.deliveryStatus || order.orderStatus || order.status,
        paymentStatus: localPaymentStatus.isPaid ? 'paid' : 'unpaid'
      });

      const response = await orderAssignmentAPI.confirmDelivery(
        userId,
        order.id,
        order.orderType,
        {
          deliveryTime: new Date().toISOString(),
          deliveryType,  
          notes: ''
        },
        token
      );

      console.log('✅ Delivery response:', response.data);

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Delivery confirmed successfully!',
          severity: 'success'
        });

        setTimeout(() => {
          onDeliveryConfirmed(response.data.data);
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Delivery confirmation failed');
      }

    } catch (error) {
      console.error('❌ Delivery confirmation error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to confirm delivery';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <IconButton
        onClick={onBack}
        disabled={loading}
        sx={{ p: 1 }}
      >
        <ArrowBack sx={{ fontSize: 36 }} />
      </IconButton>

      <Card elevation={3}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#006D77' }}>
            Confirm {deliveryType === 'report' ? 'Report' : ''} Delivery
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Delivery Type Badge */}
          {deliveryType === 'report' && (
            <Chip 
              label="REPORT DELIVERY" 
              color="secondary" 
              sx={{ mb: 2, fontWeight: 600 }}
            />
          )}

          {/* Payment Required Warning */}
          {needsPayment && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<Payment />}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Payment Collection Required
              </Typography>
              <Typography variant="body2">
                Please collect payment of <strong>₹{order.totalAmount}</strong> from the customer before confirming delivery.
              </Typography>
            </Alert>
          )}

          {/* ✅ NEW: Payment Success Alert */}
          {localPaymentStatus.paymentCollected && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
              <Typography variant="body2" fontWeight={600}>
                ✅ Payment Collected Successfully!
              </Typography>
              <Typography variant="body2">
                You can now proceed to confirm delivery.
              </Typography>
            </Alert>
          )}

          {/* Validation Warnings */}
          {!isPickedUp && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Cannot proceed:</strong> {deliveryType === 'report' ? 'Report' : 'Order'} must be picked up before delivery.
              <br />
              Current status: <strong>{order.deliveryStatus || order.orderStatus || order.status}</strong>
            </Alert>
          )}

          {isDelivered && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This {deliveryType === 'report' ? 'report' : 'order'} has already been delivered
            </Alert>
          )}

          {/* Order Details */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Order ID:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {order.orderId}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Customer:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {order.customerName}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Phone:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                <a href={`tel:${order.customerPhone}`} style={{ color: '#006D77', textDecoration: 'none' }}>
                  {order.customerPhone}
                </a>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Current Status:
              </Typography>
              <Chip 
                label={order.deliveryStatus || order.orderStatus || order.status || 'unknown'} 
                size="small" 
                color={
                  isDelivered ? 'success' :
                  isPickedUp ? 'info' :
                  'default'
                }
              />
            </Box>

            {order.totalAmount && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Amount:
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  ₹{order.totalAmount}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Payment Status:
              </Typography>
              <Chip 
                label={localPaymentStatus.isPaid || localPaymentStatus.paymentCollected ? 'PAID' : 'PENDING'} 
                size="small" 
                color={localPaymentStatus.isPaid || localPaymentStatus.paymentCollected ? 'success' : 'error'}
              />
            </Box>

            {order.paymentMethod && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Method:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {order.paymentMethod.toUpperCase()}
                </Typography>
              </Box>
            )}

            {order.pickupTime && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Picked Up At:
                </Typography>
                <Typography variant="body2">
                  {new Date(order.pickupTime).toLocaleString('en-IN')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Photo Upload Section */}
          {isPickedUp && !isDelivered && (
            <Box sx={{ mb: 3, p: 2, border: '2px dashed #ccc', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Upload Delivery Photo (Optional)
              </Typography>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  capture="environment"
                  onChange={handlePhotoChange}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CameraAlt />}
                    size="small"
                  >
                    Take Photo
                  </Button>
                </label>

                {selectedPhoto && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleUploadPhoto}
                    disabled={loading}
                  >
                    Upload
                  </Button>
                )}
              </Stack>

              {photoPreview && (
                <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                  <img 
                    src={photoPreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px',
                      borderRadius: '8px'
                    }} 
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      bgcolor: 'rgba(255,255,255,0.8)'
                    }}
                    onClick={() => {
                      setSelectedPhoto(null);
                      setPhotoPreview(null);
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {/* Delivery Instructions */}
          {canDeliver && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#e7f3ff', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Before confirming delivery:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Verify customer identity</li>
                  <li>Hand over the {deliveryType === 'report' ? 'report' : 'package'}</li>
                  <li>Get customer confirmation</li>
                  {(localPaymentStatus.isPaid || localPaymentStatus.paymentCollected) && (
                    <li><Chip label="Payment Already Collected" size="small" color="success" /></li>
                  )}
                </ul>
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack spacing={2}>
            {/* Collect Payment Button */}
            {needsPayment && isPickedUp && !isDelivered && (
              <Button
                variant="contained"
                color="success"
                onClick={handleOpenPaymentDialog}
                disabled={loading}
                startIcon={<Payment />}
                fullWidth
                sx={{
                  bgcolor: '#28a745',
                  '&:hover': { bgcolor: '#218838' }
                }}
              >
                Collect Payment - ₹{order.totalAmount}
              </Button>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              {canDeliver && onReportIssue && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => onReportIssue(order)}
                  disabled={loading}
                  startIcon={<ReportProblem />}
                  fullWidth
                >
                  Report Issue
                </Button>
              )}
              
              <Button 
                variant="contained" 
                onClick={handleConfirmDelivery}
                disabled={loading || !canDeliver}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                fullWidth
                sx={{
                  bgcolor: canDeliver ? '#006D77' : '#ccc',
                  '&:hover': { bgcolor: canDeliver ? '#005561' : '#ccc' },
                  '&:disabled': { bgcolor: '#ccc', color: '#666' }
                }}
              >
                {loading ? 'Confirming...' : 
                 !isPickedUp ? 'Pickup Required First' :
                 needsPayment ? 'Collect Payment First' :
                 'Confirm Delivery'}
              </Button>
            </Box>
          </Stack>

          {/* Help Text */}
          {!canDeliver && !isPickedUp && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Please confirm pickup first before delivery
            </Typography>
          )}

          {needsPayment && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Payment must be collected before delivery confirmation
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Payment Collection Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => !paymentLoading && setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Collect Payment
          <IconButton
            onClick={() => !paymentLoading && setPaymentDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={paymentLoading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Amount: ₹{paymentAmount}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" fontWeight={600} gutterBottom>
              Select Payment Method *
            </Typography>

            <ButtonGroup fullWidth sx={{ mb: 3 }}>
              <Button
                variant={paymentMethod === 'cash' ? 'contained' : 'outlined'}
                onClick={() => setPaymentMethod('cash')}
                sx={{
                  ...(paymentMethod === 'cash' && {
                    bgcolor: '#28a745',
                    '&:hover': { bgcolor: '#218838' }
                  })
                }}
              >
                CASH
              </Button>
              <Button
                variant={paymentMethod === 'card' ? 'contained' : 'outlined'}
                onClick={() => setPaymentMethod('card')}
                sx={{
                  ...(paymentMethod === 'card' && {
                    bgcolor: '#007bff',
                    '&:hover': { bgcolor: '#0056b3' }
                  })
                }}
              >
                CARD
              </Button>
              <Button
                variant={paymentMethod === 'upi' ? 'contained' : 'outlined'}
                onClick={() => setPaymentMethod('upi')}
                startIcon={<QrCode2 />}
                sx={{
                  ...(paymentMethod === 'upi' && {
                    bgcolor: '#6c757d',
                    '&:hover': { bgcolor: '#5a6268' }
                  })
                }}
              >
                UPI
              </Button>
            </ButtonGroup>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(parseFloat(e.target.value))}
              disabled
              sx={{ mb: 2 }}
            />

            <Alert severity="info" icon={<Payment />}>
              <Typography variant="body2">
                Please verify the amount with the customer before collecting payment
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setPaymentDialogOpen(false)} 
            disabled={paymentLoading}
          >
            Cancel
          </Button>

          {onReportIssue && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setPaymentDialogOpen(false);
                onReportIssue(order);
              }}
              disabled={paymentLoading}
              startIcon={<ReportProblem />}
            >
              Report Issue
            </Button>
          )}

          <Button 
            variant="contained" 
            onClick={handleCollectPayment}
            disabled={paymentLoading || !paymentMethod}
            startIcon={paymentLoading ? <CircularProgress size={20} color="inherit" /> : <Payment />}
            sx={{
              bgcolor: '#28a745',
              '&:hover': { bgcolor: '#218838' }
            }}
          >
            {paymentLoading ? 'Processing...' : 'Confirm Payment Received'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryVerification;